import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import TextTemplatePicker from '../../../TextTemplatePicker';
import DocumentTemplateSelector from '../../../DocumentTemplateSelector';
import EmailModal from '../../../EmailModal';
import { apiFetch } from '../../../utils/api';
import { generateDocumentOutput, generateDocumentPdfDataUri, previewDocumentOutput } from '../../../utils/documentGeneration';
import { getCompanyVariables } from '../../../utils/templateVariables';
import { navigateWithPreference } from '../../../utils/navigationHelper';

function isSeededDocumentSample(template) {
  return String(template.original_filename || '').toLowerCase().startsWith('sample_')
    && String(template.description || '').toLowerCase().startsWith('sample ');
}

function pickInitialDocumentTemplate(templates) {
  const customTemplates = templates
    .filter((template) => !isSeededDocumentSample(template))
    .sort((a, b) => new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0));

  return customTemplates.find((template) => template.is_default)
    || customTemplates[0]
    || templates.find((template) => template.is_default)
    || templates[0]
    || null;
}

function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [invoice, setInvoice] = useState({
    customer_id: '',
    site_id: '',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: '',
    status: 'draft',
    customer_po_number: '',
    quote_number: '',
    job_number: '',
    job_id: '',
    quote_id: '',
    location: '',
    works_carried_out: '',
    notes: '',
    terms: 'Payment due within 30 days.\nAll prices exclude VAT unless stated.\nInvoice valid for 30 days from date of issue.',
    items: [],
    // New accounting fields
    bank_details_type: 'ours',
    remittance_email: '',
    is_cis_invoice: false,
    cis_rate: 0,
    cis_deduction: 0,
    cis_net_payment: 0
  });

  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDocumentTemplateSelector, setShowDocumentTemplateSelector] = useState(false);
  const [documentTemplates, setDocumentTemplates] = useState([]);
  const [selectedDocumentTemplate, setSelectedDocumentTemplate] = useState(null);
  const [emailDocumentTemplate, setEmailDocumentTemplate] = useState(null);
  const [documentTemplateError, setDocumentTemplateError] = useState('');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [customerEmail, setCustomerEmail] = useState('');
  const [emailTemplateData, setEmailTemplateData] = useState({});
  const [companySettings, setCompanySettings] = useState({});
  const [selectedCustomerData, setSelectedCustomerData] = useState(null);

  // Product selector state
  const [products, setProducts] = useState([]);
  const [showProductSelector, setShowProductSelector] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLetter, setSelectedLetter] = useState('all');
  const [quickQuantities, setQuickQuantities] = useState({});
  const [addedNotification, setAddedNotification] = useState(null);

  // Labor selector state
  const [laborCosts, setLaborCosts] = useState([]);
  const [showLaborSelector, setShowLaborSelector] = useState(false);
  const [laborSearch, setLaborSearch] = useState('');
  const [laborQuantities, setLaborQuantities] = useState({});

  // Consumable selector state
  const [consumables, setConsumables] = useState([]);
  const [showConsumableSelector, setShowConsumableSelector] = useState(false);
  const [consumableSearch, setConsumableSearch] = useState('');
  const [consumableQuantities, setConsumableQuantities] = useState({});
  const [selectedConsumableCategory, setSelectedConsumableCategory] = useState('all');
  const [selectedConsumableLetter, setSelectedConsumableLetter] = useState('all');

  useEffect(() => {
    fetchCustomers();
    fetchCompanySettings();
    fetchProducts();
    fetchLaborCosts();
    fetchConsumables();
    if (!isNew) {
      fetchInvoice();
      fetchDocumentTemplates();
    }
  }, [id]);

  // Auto-populate customer accounting info when customer changes
  useEffect(() => {
    if (invoice.customer_id && customers.length > 0) {
      const customer = customers.find(c => c.id === parseInt(invoice.customer_id));
      if (customer) {
        setSelectedCustomerData(customer);

        // Auto-populate accounting fields on new invoice
        if (isNew) {
          const updates = {
            customer_account_ref: customer.account_reference,
            customer_credit_limit: customer.credit_limit,
            customer_payment_terms: customer.payment_terms || 'Net 30',
            is_cis_invoice: customer.is_cis_contractor || false,
            cis_rate: customer.is_cis_contractor ? (customer.cis_rate || 20) : 0,
            remittance_email: customer.accounts_contact_email || customer.email || ''
          };

          setInvoice(prev => ({ ...prev, ...updates }));

          // Calculate due date from payment terms
          if (customer.payment_terms) {
            calculateDueDate(invoice.invoice_date, customer.payment_terms);
          }
        }
      }
    }
  }, [invoice.customer_id, customers]);

  // Recalculate CIS when totals change
  useEffect(() => {
    if (invoice.is_cis_invoice && invoice.cis_rate > 0) {
      const totals = calculateTotals();
      const cisDeduction = totals.subtotal * (invoice.cis_rate / 100);
      const cisNetPayment = totals.total - cisDeduction;

      setInvoice(prev => ({
        ...prev,
        cis_deduction: cisDeduction,
        cis_net_payment: cisNetPayment
      }));
    }
  }, [invoice.items, invoice.is_cis_invoice, invoice.cis_rate]);

  useEffect(() => {
    if (invoice.customer_id) {
      fetchSites(invoice.customer_id);
    }
  }, [invoice.customer_id]);

  // Auto-populate remittance email when bank details type changes
  useEffect(() => {
    if (companySettings && invoice.bank_details_type) {
      const remittanceEmail = invoice.bank_details_type === 'ecap'
        ? (companySettings.ecap_remittance_email || '')
        : (companySettings.bank_remittance_email || '');

      setInvoice(prev => ({ ...prev, remittance_email: remittanceEmail }));
    }
  }, [invoice.bank_details_type, companySettings]);

  const fetchCompanySettings = async () => {
    try {
      const res = await apiFetch('/api/company-settings');
      const data = await res.json();
      setCompanySettings(data);
    } catch (error) {
      console.error('Error fetching company settings:', error);
    }
  };

  const fetchDocumentTemplates = async () => {
    try {
      setDocumentTemplateError('');
      const placementResponse = await apiFetch('/api/documents/placements/invoice.print_pdf/resolve');
      const placement = await placementResponse.json();
      if (!placementResponse.ok) {
        throw new Error(placement.error || 'Invoice template placement is not available');
      }

      const libraryKey = placement.library_key || 'invoice';
      const templatesResponse = await apiFetch(`/api/documents/templates?type=${encodeURIComponent(libraryKey)}`);
      const templates = await templatesResponse.json();
      if (!templatesResponse.ok || !Array.isArray(templates)) {
        throw new Error(templates.error || 'Invoice document templates could not load');
      }

      setDocumentTemplates(templates);
      setSelectedDocumentTemplate(pickInitialDocumentTemplate(templates));
    } catch (error) {
      console.error('Error fetching invoice document templates:', error);
      setDocumentTemplates([]);
      setSelectedDocumentTemplate(null);
      setDocumentTemplateError(error.message || 'Invoice document templates could not load');
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await apiFetch('/api/customers');
      const data = await response.json();
      setCustomers(data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchSites = async (customerId) => {
    try {
      const response = await apiFetch(`/api/sites?customer_id=${customerId}`);
      const data = await response.json();
      setSites(data);
    } catch (error) {
      console.error('Error fetching sites:', error);
    }
  };

  const fetchInvoice = async () => {
    try {
      const response = await apiFetch(`/api/invoices/${id}`);
      const data = await response.json();

      if (data.invoice_date) data.invoice_date = data.invoice_date.split('T')[0];
      if (data.due_date) data.due_date = data.due_date.split('T')[0];

      setInvoice(data);
    } catch (error) {
      console.error('Error fetching invoice:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await apiFetch('/api/products');
      const data = await response.json();
      setProducts(data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchLaborCosts = async () => {
    try {
      const response = await apiFetch('/api/products');
      const data = await response.json();
      // Filter for services (labor)
      const services = data.filter(p => p.type === 'Service');
      setLaborCosts(services);
    } catch (error) {
      console.error('Error fetching labor costs:', error);
    }
  };

  const fetchConsumables = async () => {
    try {
      const response = await apiFetch('/api/products');
      const data = await response.json();
      // Filter for consumables
      const consumableItems = data.filter(p => p.type === 'Consumable');
      setConsumables(consumableItems);
    } catch (error) {
      console.error('Error fetching consumables:', error);
    }
  };

  // Parse payment terms ("Net 30" → 30 days, etc.)
  const parsePaymentTerms = (terms) => {
    if (!terms) return 30; // Default
    const match = terms.match(/Net\s*(\d+)/i);
    return match ? parseInt(match[1]) : 30;
  };

  // Calculate due date from invoice date + payment terms
  const calculateDueDate = (invoiceDate, paymentTerms) => {
    if (!invoiceDate) return;

    const days = parsePaymentTerms(paymentTerms);
    const date = new Date(invoiceDate);
    date.setDate(date.getDate() + days);

    setInvoice(prev => ({
      ...prev,
      due_date: date.toISOString().split('T')[0],
      payment_due_date: date.toISOString().split('T')[0]
    }));
  };

  // Recalculate due date when invoice date changes
  const handleInvoiceDateChange = (newDate) => {
    setInvoice(prev => ({ ...prev, invoice_date: newDate }));
    if (invoice.customer_payment_terms) {
      calculateDueDate(newDate, invoice.customer_payment_terms);
    }
  };

  const addLineItem = () => {
    setInvoice(prev => ({
      ...prev,
      items: [
        ...prev.items,
        {
          description: '',
          category: 'parts',
          quantity: 1,
          unit: 'each',
          unit_price: 0
        }
      ]
    }));
  };

  const addProductToInvoice = (product) => {
    const qty = quickQuantities[product.id] || 1;
    setInvoice(prev => ({
      ...prev,
      items: [
        ...prev.items,
        {
          product_id: product.id,
          product_code: product.product_code,
          description: product.product_name,
          category: 'parts', // Products are always parts
          quantity: qty,
          unit: 'each',
          unit_price: parseFloat(product.supply_price || product.price || 0)
        }
      ]
    }));

    // Show notification
    setAddedNotification(`Added ${qty}x ${product.product_name}`);
    setTimeout(() => setAddedNotification(null), 2000);

    // Keep modal open for adding more products - just reset search and qty
    setProductSearch('');
    setQuickQuantities(prev => ({ ...prev, [product.id]: 1 }));
  };

  const addLaborToInvoice = (labor) => {
    const qty = laborQuantities[labor.id] || 1;
    setInvoice(prev => ({
      ...prev,
      items: [
        ...prev.items,
        {
          product_id: labor.id,
          product_code: labor.product_code,
          description: labor.product_name,
          category: 'labor',
          quantity: qty,
          unit: labor.unit || 'hours',
          unit_price: parseFloat(labor.price || 0)
        }
      ]
    }));

    // Show notification
    setAddedNotification(`Added ${qty}h ${labor.product_name}`);
    setTimeout(() => setAddedNotification(null), 2000);

    setLaborSearch('');
    setLaborQuantities(prev => ({ ...prev, [labor.id]: 1 }));
  };

  const addConsumableToInvoice = (consumable) => {
    const qty = consumableQuantities[consumable.id] || 1;
    setInvoice(prev => ({
      ...prev,
      items: [
        ...prev.items,
        {
          product_id: consumable.id,
          product_code: consumable.product_code,
          description: consumable.product_name,
          category: 'consumable',
          quantity: qty,
          unit: consumable.unit || 'each',
          unit_price: parseFloat(consumable.supply_price || consumable.price || 0)
        }
      ]
    }));

    // Show notification
    setAddedNotification(`Added ${qty}x ${consumable.product_name}`);
    setTimeout(() => setAddedNotification(null), 2000);

    setConsumableSearch('');
    setConsumableQuantities(prev => ({ ...prev, [consumable.id]: 1 }));
  };

  const updateLineItem = (index, field, value) => {
    const newItems = [...invoice.items];
    newItems[index][field] = value;
    setInvoice(prev => ({ ...prev, items: newItems }));
  };

  const removeLineItem = (index) => {
    setInvoice(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const calculateLineTotals = (item) => {
    const qty = parseFloat(item.quantity) || 0;
    const price = parseFloat(item.unit_price) || 0;
    const lineTotal = qty * price;
    return { lineTotal };
  };

  const calculateTotals = () => {
    let partsTotal = 0;
    let labourTotal = 0;

    invoice.items.forEach(item => {
      const { lineTotal } = calculateLineTotals(item);
      if (item.category === 'labour' || item.category === 'labor') {
        labourTotal += lineTotal;
      } else {
        // Parts includes: "parts", "consumable", and "other"
        partsTotal += lineTotal;
      }
    });

    const subtotal = partsTotal + labourTotal;
    const vatRate = 20.00;
    const vatAmount = subtotal * (vatRate / 100);
    const total = subtotal + vatAmount;

    return { partsTotal, labourTotal, subtotal, vatAmount, total };
  };

  const handleSave = async () => {
    if (!invoice.customer_id) {
      alert('Please select a customer');
      return;
    }

    setSaving(true);
    try {
      const url = isNew
        ? '/api/invoices'
        : `/api/invoices/${id}`;

      const method = isNew ? 'POST' : 'PUT';

      // Calculate totals before saving
      const totals = calculateTotals();
      const invoiceData = {
        ...invoice,
        subtotal: totals.subtotal,
        vat_amount: totals.vatAmount,
        total: totals.total,
        balance: totals.total - (parseFloat(invoice.paid_amount) || 0)
      };

      const response = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(invoiceData)
      });

      if (response.ok) {
        const data = await response.json();
        if (isNew && data.id) {
          navigate(`/finance/invoices/${data.id}`);
        } else {
          alert('Invoice saved successfully');
          fetchInvoice();
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(`Failed to save invoice${errorData.error ? `: ${errorData.error}` : ''}`);
      }
    } catch (error) {
      console.error('Error saving invoice:', error);
      alert(`Error saving invoice${error.message ? `: ${error.message}` : ''}`);
    } finally {
      setSaving(false);
    }
  };

  const generateSelectedDocumentTemplate = async (action) => {
    if (!selectedDocumentTemplate) {
      setShowDocumentTemplateSelector(true);
      return;
    }

    let outputWindow = null;
    if (action === 'print') {
      outputWindow = window.open('', '_blank');
      if (outputWindow) {
        outputWindow.document.write('<p style="font-family: sans-serif; padding: 20px;">Preparing document...</p>');
      }
    }

    await handleDocumentTemplateGenerate(selectedDocumentTemplate, action, null, outputWindow);
  };

  const handlePrint = async () => {
    await generateSelectedDocumentTemplate('print');
  };

  const handlePDF = async () => {
    await generateSelectedDocumentTemplate('pdf');
  };

  const handlePreview = async () => {
    if (!selectedDocumentTemplate) {
      setShowDocumentTemplateSelector(true);
      return;
    }

    const previewWindow = window.open('', '_blank');
    if (previewWindow) {
      previewWindow.document.write('<p style="font-family: sans-serif; padding: 20px;">Preparing preview...</p>');
    }

    try {
      const templateData = await prepareInvoiceData(invoice);
      await previewDocumentOutput(selectedDocumentTemplate, templateData, `Invoice-${invoice.invoice_ref || invoice.id}`, previewWindow);
    } catch (error) {
      if (previewWindow && !previewWindow.closed) previewWindow.close();
      console.error('Document preview error:', error);
      alert('Error previewing document: ' + error.message);
    }
  };

  const handleDocumentTemplateGenerate = async (template, action, data, outputWindow = null) => {
    try {
      if (action === 'email') {
        setSelectedDocumentTemplate(template);
        setShowDocumentTemplateSelector(false);
        await handleEmail(template);
        return;
      }

      const templateData = await prepareInvoiceData(invoice);
      await generateDocumentOutput(template, action, templateData, `Invoice-${invoice.invoice_ref || invoice.id}`, outputWindow);
      setSelectedDocumentTemplate(template);
      setShowDocumentTemplateSelector(false);
    } catch (error) {
      console.error('Document generation error:', error);
      alert('Error generating document: ' + error.message);
    }
  };

  const handleEmail = async (templateOverride = selectedDocumentTemplate) => {
    if (!templateOverride) {
      setShowDocumentTemplateSelector(true);
      return;
    }

    setSelectedDocumentTemplate(templateOverride);
    setEmailDocumentTemplate(templateOverride);

    const customer = selectedCustomerData || customers.find(c => c.id === parseInt(invoice.customer_id));
    const email = invoice.remittance_email || invoice.customer_email || customer?.accounts_contact_email || customer?.email || '';

    if (!email) {
      const confirmed = window.confirm('No email address found for this customer. Do you want to continue anyway?');
      if (!confirmed) return;
    }

    const templateData = await prepareInvoiceData(invoice);
    setCustomerEmail(email);
    setEmailTemplateData({
      ...templateData,
      document_number: templateData.invoice_number,
      invoice_total: templateData.total,
    });
    setShowEmailModal(true);
  };

  const handleSendEmail = async ({ to, subject, body }) => {
    const templateForEmail = emailDocumentTemplate || selectedDocumentTemplate;
    if (!templateForEmail?.id) {
      throw new Error('Please select an invoice Word template first');
    }

    const templateData = await prepareInvoiceData(invoice);
    const pdfData = await generateDocumentPdfDataUri(templateForEmail, templateData);

    const response = await apiFetch('/api/email/send', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        to,
        subject,
        body,
        pdfData,
        pdfFilename: `Invoice-${invoice.invoice_ref || invoice.id}.pdf`,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error([error.error, error.details].filter(Boolean).join(': ') || 'Failed to send email');
    }

    alert('Invoice email sent successfully');
  };

  const prepareInvoiceData = async (invoiceData) => {
    // Get bank details based on type
    let bankDetails = '';
    let bankLegalText = '';

    if (invoiceData.bank_details_type === 'ecap') {
      // eCap bank details
      const ecapParts = [];
      if (companySettings.ecap_bank_name) ecapParts.push(`Bank: ${companySettings.ecap_bank_name}`);
      if (companySettings.ecap_account_name) ecapParts.push(`Account Name: ${companySettings.ecap_account_name}`);
      if (companySettings.ecap_sort_code) ecapParts.push(`Sort Code: ${companySettings.ecap_sort_code}`);
      if (companySettings.ecap_account_number) ecapParts.push(`Account Number: ${companySettings.ecap_account_number}`);
      if (companySettings.ecap_iban) ecapParts.push(`IBAN: ${companySettings.ecap_iban}`);
      if (companySettings.ecap_swift) ecapParts.push(`SWIFT: ${companySettings.ecap_swift}`);
      bankDetails = ecapParts.join('\n');
      bankLegalText = companySettings.ecap_legal_text || '';
    } else {
      // Our bank details
      const bankParts = [];
      if (companySettings.bank_name) bankParts.push(`Bank: ${companySettings.bank_name}`);
      if (companySettings.bank_account_name) bankParts.push(`Account Name: ${companySettings.bank_account_name}`);
      if (companySettings.bank_sort_code) bankParts.push(`Sort Code: ${companySettings.bank_sort_code}`);
      if (companySettings.bank_account_number) bankParts.push(`Account Number: ${companySettings.bank_account_number}`);
      if (companySettings.bank_iban) bankParts.push(`IBAN: ${companySettings.bank_iban}`);
      if (companySettings.bank_swift) bankParts.push(`SWIFT: ${companySettings.bank_swift}`);
      bankDetails = bankParts.join('\n');
    }

    return {
      customer_name: invoiceData.customer_name || '',
      customer_address: invoiceData.customer_address || '',
      customer_email: invoiceData.customer_email || '',
      customer_phone: invoiceData.customer_phone || '',
      client_name: invoiceData.customer_name || '',
      client_company: invoiceData.customer_name || '',
      client_address: invoiceData.customer_address || '',
      client_email: invoiceData.customer_email || '',
      client_phone: invoiceData.customer_phone || '',
      invoice_number: invoiceData.invoice_ref || invoiceData.id || '',
      document_number: invoiceData.invoice_ref || invoiceData.id || '',
      document_date: invoiceData.invoice_date ? new Date(invoiceData.invoice_date).toLocaleDateString() : '',
      invoice_date: invoiceData.invoice_date ? new Date(invoiceData.invoice_date).toLocaleDateString() : '',
      due_date: invoiceData.due_date ? new Date(invoiceData.due_date).toLocaleDateString() : '',
      payment_terms: invoiceData.customer_payment_terms || '',
      customer_po_number: invoiceData.customer_po_number || '',
      quote_number: invoiceData.quote_number || '',
      job_number: invoiceData.job_number || '',
      site_address: invoiceData.site_address || '',
      location: invoiceData.location || '',
      works_carried_out: invoiceData.works_carried_out || '',
      items: (invoiceData.items || []).map(item => ({
        description: item.description || '',
        quantity: item.quantity || 0,
        unit_price: item.unit_price || 0,
        total: (item.quantity || 0) * (item.unit_price || 0)
      })),
      subtotal: `£${parseFloat(invoiceData.subtotal || 0).toFixed(2)}`,
      vat_rate: invoiceData.vat_rate || '20',
      vat_amount: `£${parseFloat(invoiceData.vat_amount || 0).toFixed(2)}`,
      total: `£${parseFloat(invoiceData.total || 0).toFixed(2)}`,

      // CIS fields
      is_cis_invoice: invoiceData.is_cis_invoice || false,
      cis_rate: invoiceData.cis_rate || 0,
      cis_deduction: invoiceData.cis_deduction ? `£${parseFloat(invoiceData.cis_deduction).toFixed(2)}` : '£0.00',
      cis_net_payment: invoiceData.cis_net_payment ? `£${parseFloat(invoiceData.cis_net_payment).toFixed(2)}` : null,

      // Bank details (formatted block)
      bank_details: bankDetails,
      bank_legal_text: bankLegalText,

      // Individual bank fields (for custom formatting in templates)
      bank_name: invoiceData.bank_details_type === 'ecap' ? (companySettings.ecap_bank_name || '') : (companySettings.bank_name || ''),
      bank_account_name: invoiceData.bank_details_type === 'ecap' ? (companySettings.ecap_account_name || '') : (companySettings.bank_account_name || ''),
      bank_sort_code: invoiceData.bank_details_type === 'ecap' ? (companySettings.ecap_sort_code || '') : (companySettings.bank_sort_code || ''),
      bank_account_number: invoiceData.bank_details_type === 'ecap' ? (companySettings.ecap_account_number || '') : (companySettings.bank_account_number || ''),
      bank_iban: invoiceData.bank_details_type === 'ecap' ? (companySettings.ecap_iban || '') : (companySettings.bank_iban || ''),
      bank_swift: invoiceData.bank_details_type === 'ecap' ? (companySettings.ecap_swift || '') : (companySettings.bank_swift || ''),

      remittance_email: invoiceData.remittance_email || '',

      terms: invoiceData.terms || '',
      notes: invoiceData.notes || '',

      // Company fields (consistent across all templates)
      ...getCompanyVariables(companySettings)
    };
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete invoice ${invoice.invoice_ref || 'this invoice'}? This action cannot be undone.`)) {
      return;
    }

    setDeleting(true);
    try {
      const res = await apiFetch(`/api/invoices/${id}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        alert('Invoice deleted successfully!');
        navigate('/finance/invoices');
      } else {
        alert('Failed to delete invoice');
      }
    } catch (err) {
      console.error('Error deleting invoice:', err);
      alert('Error deleting invoice');
    }
    setDeleting(false);
  };

  const totals = calculateTotals();
  const selectedCustomer = selectedCustomerData || customers.find(c => c.id === parseInt(invoice.customer_id));
  const selectedSite = sites.find(s => s.id === parseInt(invoice.site_id));

  // Check if over credit limit
  const isOverCreditLimit = selectedCustomer?.credit_limit && totals.total > parseFloat(selectedCustomer.credit_limit);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md sticky top-0 z-10" style={{ height: '116px' }}>
        <div className="max-w-full mx-auto px-6 h-full flex flex-col justify-between py-3">
          {/* Company Name - Top Left */}
          {companySettings.company_name && (
            <div className="text-white text-sm font-medium opacity-90">
              {companySettings.company_name}
            </div>
          )}

          {/* Main Title Area */}
          <div className="flex items-center justify-between flex-1">
            <button
              onClick={() => navigate('/finance/invoices')}
              className="text-white hover:text-blue-100 transition text-lg font-semibold"
            >
              ⬅ Back
            </button>
            <div className="flex-1 text-center">
              <div className="flex items-center justify-center gap-3">
                <h1 className="text-4xl font-bold">
                  {isNew ? 'New Invoice' : `Invoice ${invoice.invoice_ref || ''}`}
                </h1>
                {invoice.is_cis_invoice && (
                  <span className="px-3 py-1 bg-orange-500 text-white rounded-full text-sm font-semibold">
                    CIS Invoice
                  </span>
                )}
              </div>
              <span className="text-sm text-blue-100">{invoice.status || 'Draft'}</span>
            </div>
            <div className="flex items-center justify-end gap-2 flex-wrap max-w-[620px]">
              {!isNew && invoice.job_number && (
                <button
                  onClick={() => navigate(`/jobs/${invoice.job_id_full}`)}
                  className="px-4 py-1.5 bg-green-600 text-white rounded text-sm font-medium hover:bg-green-700 transition"
                >
                  📋 Job: {invoice.job_number}
                </button>
              )}
              {!isNew && invoice.quote_number && !invoice.job_number && (
                <button
                  onClick={() => navigate(`/ops/quotes/${invoice.quote_id_full}`)}
                  className="px-4 py-1.5 bg-green-600 text-white rounded text-sm font-medium hover:bg-green-700 transition"
                >
                  📄 Quote: {invoice.quote_number}
                </button>
              )}
              {!isNew && (
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="px-4 py-1.5 bg-red-600 text-white rounded text-sm font-medium hover:bg-red-700 transition disabled:opacity-50"
                >
                  {deleting ? 'Deleting...' : '🗑️ Delete'}
                </button>
              )}
              {!isNew && (
                <>
                  <label className="flex items-center gap-1 px-2 py-1 bg-white text-black rounded border border-gray-300 shadow-sm max-w-[190px]">
                    <span className="text-xs font-semibold uppercase text-gray-600">Template</span>
                    <select
                      value={selectedDocumentTemplate?.id || ''}
                      onChange={(event) => {
                        const template = documentTemplates.find((item) => item.id === event.target.value);
                        setSelectedDocumentTemplate(template || null);
                      }}
                      disabled={documentTemplates.length === 0}
                      title={documentTemplateError || 'Invoice Word template'}
                      className="bg-white text-black text-sm font-medium outline-none w-[115px]"
                    >
                      {documentTemplates.length === 0 ? (
                        <option value="">{documentTemplateError || 'No Word templates'}</option>
                      ) : (
                        documentTemplates.map((template) => (
                          <option key={template.id} value={template.id}>
                            {template.name}{template.is_default ? ' (Default)' : ''}
                          </option>
                        ))
                      )}
                    </select>
                  </label>
                  <button
                    onClick={handlePreview}
                    className="px-4 py-1.5 bg-gray-300 text-black rounded text-sm font-medium hover:bg-gray-400 transition"
                  >
                    Preview
                  </button>
                  <button
                    onClick={handlePrint}
                    className="px-4 py-1.5 bg-gray-300 text-black rounded text-sm font-medium hover:bg-gray-400 transition"
                  >
                    Print
                  </button>
                  <button
                    onClick={handlePDF}
                    className="px-4 py-1.5 bg-gray-300 text-black rounded text-sm font-medium hover:bg-gray-400 transition"
                  >
                    PDF
                  </button>
                  <button
                    onClick={handleEmail}
                    className="px-4 py-1.5 bg-gray-300 text-black rounded text-sm font-medium hover:bg-gray-400 transition"
                  >
                    Email
                  </button>
                </>
              )}
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm shadow-sm disabled:opacity-50"
              >
                {saving ? '⏳ Saving...' : '💾 Save'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto p-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Main Content - 2/3 width */}
          <div className="lg:col-span-2 space-y-4">

            {/* Section 1: Invoice Details */}
            <div className="bg-blue-50 rounded-lg shadow p-6 border-l-4 border-blue-400">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <span className="bg-blue-100 text-blue-700 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-3">1</span>
                Invoice Details
              </h2>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Customer *</label>
                  <select
                    value={invoice.customer_id}
                    onChange={(e) => setInvoice(prev => ({ ...prev, customer_id: e.target.value, site_id: '' }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-indigo-500"
                  >
                    <option value="">Select customer...</option>
                    {customers.map(c => (
                      <option key={c.id} value={c.id}>{c.company_name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Site Location</label>
                  <select
                    value={invoice.site_id || ''}
                    onChange={(e) => setInvoice(prev => ({ ...prev, site_id: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-indigo-500"
                    disabled={!invoice.customer_id}
                  >
                    <option value="">Head Office</option>
                    {sites.map(s => (
                      <option key={s.id} value={s.id}>{s.site_name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Customer Order Number</label>
                  <input
                    type="text"
                    value={invoice.customer_po_number || ''}
                    onChange={(e) => setInvoice(prev => ({ ...prev, customer_po_number: e.target.value }))}
                    placeholder="Customer's PO/Order ref..."
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quote Reference</label>
                  {invoice.quote_id ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={invoice.quote_number || ''}
                        onChange={(e) => setInvoice(prev => ({ ...prev, quote_number: e.target.value }))}
                        placeholder="Quote number..."
                        className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-indigo-500"
                      />
                      <button
                        onClick={() => navigateWithPreference(navigate, `/ops/quotes/${invoice.quote_id}`, 'quotes')}
                        className="px-3 py-2 bg-blue-100 text-blue-700 rounded text-sm font-medium hover:bg-blue-200 transition"
                        title="Open linked quote"
                      >
                        🔗 View
                      </button>
                    </div>
                  ) : (
                    <input
                      type="text"
                      value={invoice.quote_number || ''}
                      onChange={(e) => setInvoice(prev => ({ ...prev, quote_number: e.target.value }))}
                      placeholder="Quote number (if linked)..."
                      className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-indigo-500"
                    />
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Job Reference</label>
                  {invoice.job_id ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={invoice.job_number || ''}
                        onChange={(e) => setInvoice(prev => ({ ...prev, job_number: e.target.value }))}
                        placeholder="Job number..."
                        className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-indigo-500"
                      />
                      <button
                        onClick={() => navigateWithPreference(navigate, `/jobs/${invoice.job_id}`, 'jobs')}
                        className="px-3 py-2 bg-blue-100 text-blue-700 rounded text-sm font-medium hover:bg-blue-200 transition"
                        title="Open linked job"
                      >
                        🔗 View
                      </button>
                    </div>
                  ) : (
                    <input
                      type="text"
                      value={invoice.job_number || ''}
                      onChange={(e) => setInvoice(prev => ({ ...prev, job_number: e.target.value }))}
                      placeholder="Job number (if linked)..."
                      className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-indigo-500"
                    />
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Date</label>
                  <input
                    type="date"
                    value={invoice.invoice_date || ''}
                    onChange={(e) => handleInvoiceDateChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={invoice.status}
                    onChange={(e) => setInvoice(prev => ({ ...prev, status: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-indigo-500"
                  >
                    <option value="draft">Draft</option>
                    <option value="sent">Sent</option>
                    <option value="partial">Partially Paid</option>
                    <option value="paid">Paid</option>
                    <option value="overdue">Overdue</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Section 2: Location */}
            <div className="bg-blue-50 rounded-lg shadow p-6 border-l-4 border-blue-400">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <span className="bg-blue-100 text-blue-700 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-3">2</span>
                Location
              </h2>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Site Location / Address</label>
                <input
                  type="text"
                  value={invoice.location || ''}
                  onChange={(e) => setInvoice(prev => ({ ...prev, location: e.target.value }))}
                  placeholder="Enter site location or address where work was performed..."
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Where the work was carried out (can differ from customer's main address)
                </p>
              </div>
            </div>

            {/* Section 3: Works Carried Out */}
            <div className="bg-blue-50 rounded-lg shadow p-6 border-l-4 border-blue-400">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <span className="bg-blue-100 text-blue-700 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-3">3</span>
                Works Carried Out
              </h2>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Description of works completed on site</label>
                  <TextTemplatePicker
                    category="Invoices"
                    onInsert={(text) => {
                      const current = invoice.works_carried_out || '';
                      const newText = current ? current + '\n\n' + text : text;
                      setInvoice(prev => ({ ...prev, works_carried_out: newText }));
                    }}
                  />
                </div>
                <textarea
                  value={invoice.works_carried_out || ''}
                  onChange={(e) => setInvoice(prev => ({ ...prev, works_carried_out: e.target.value }))}
                  rows="6"
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500"
                  placeholder="Enter details of work completed (e.g., repairs, installations, maintenance)..."
                />
                <p className="mt-1 text-xs text-gray-500">
                  This will be shown on the invoice. Future: auto-populated from engineer's mobile app.
                </p>
              </div>
            </div>

            {/* Section 4: Line Items / Materials */}
            <div className="bg-blue-50 rounded-lg shadow p-6 border-l-4 border-blue-400">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <span className="bg-blue-100 text-blue-700 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-3">4</span>
                  Line Items / Materials
                </h2>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowProductSelector(!showProductSelector)}
                    className="px-3 py-1.5 bg-gray-300 text-black rounded text-sm font-medium hover:bg-gray-400"
                  >
                    {showProductSelector ? '✕ Close' : '📦 Add Product'}
                  </button>
                  <button
                    onClick={() => setShowLaborSelector(!showLaborSelector)}
                    className="px-3 py-1.5 bg-gray-300 text-black rounded text-sm font-medium hover:bg-gray-400"
                  >
                    {showLaborSelector ? '✕ Close' : '👷 Add Labor'}
                  </button>
                  <button
                    onClick={() => setShowConsumableSelector(!showConsumableSelector)}
                    className="px-3 py-1.5 bg-gray-300 text-black rounded text-sm font-medium hover:bg-gray-400"
                  >
                    {showConsumableSelector ? '✕ Close' : '🔧 Add Consumable'}
                  </button>
                  <button
                    onClick={addLineItem}
                    className="px-3 py-1.5 bg-gray-300 text-black rounded text-sm font-medium hover:bg-gray-400"
                  >
                    ➕ Add Manual Line
                  </button>
                </div>
              </div>

              {invoice.items.length === 0 ? (
                <div className="text-center py-8 text-gray-500 text-sm">
                  No line items yet. Click "Add from Stock" to select products or "Add Manual Line" to enter custom items.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left px-2 py-2 text-xs font-semibold text-gray-600">Description</th>
                        <th className="text-left px-2 py-2 text-xs font-semibold text-gray-600">Category</th>
                        <th className="text-center px-2 py-2 text-xs font-semibold text-gray-600">Qty</th>
                        <th className="text-right px-2 py-2 text-xs font-semibold text-gray-600">Price</th>
                        <th className="text-right px-2 py-2 text-xs font-semibold text-gray-600">Total</th>
                        <th className="text-center px-2 py-2 text-xs font-semibold text-gray-600"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {invoice.items.map((item, index) => {
                        const { lineTotal } = calculateLineTotals(item);
                        return (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-2 py-2">
                              <input
                                type="text"
                                value={item.description}
                                onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                                placeholder="Brief description..."
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                              />
                            </td>
                            <td className="px-2 py-2">
                              <select
                                value={item.category}
                                onChange={(e) => updateLineItem(index, 'category', e.target.value)}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                              >
                                <option value="parts">Parts</option>
                                <option value="labour">Labour</option>
                                <option value="other">Other</option>
                              </select>
                            </td>
                            <td className="px-2 py-2">
                              <input
                                type="number"
                                value={item.quantity}
                                onChange={(e) => updateLineItem(index, 'quantity', e.target.value)}
                                className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-center"
                                min="0"
                                step="0.01"
                              />
                            </td>
                            <td className="px-2 py-2">
                              <input
                                type="number"
                                value={item.unit_price}
                                onChange={(e) => updateLineItem(index, 'unit_price', e.target.value)}
                                className="w-24 px-2 py-1 border border-gray-300 rounded text-sm text-right"
                                min="0"
                                step="0.01"
                                placeholder="0.00"
                              />
                            </td>
                            <td className="px-2 py-2 text-right font-medium">
                              £{lineTotal.toFixed(2)}
                            </td>
                            <td className="px-2 py-2 text-center">
                              <button
                                onClick={() => removeLineItem(index)}
                                className="text-red-600 hover:text-red-800 text-xs"
                              >
                                🗑️
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Section 5: Payment & Banking */}
            <div className="bg-blue-50 rounded-lg shadow p-6 border-l-4 border-blue-400">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <span className="bg-blue-100 text-blue-700 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-3">5</span>
                Payment & Banking
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Bank Details to Show on Invoice</label>
                  <div className="flex gap-4">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="bank_details_type"
                        value="ours"
                        checked={invoice.bank_details_type === 'ours'}
                        onChange={(e) => setInvoice(prev => ({ ...prev, bank_details_type: e.target.value }))}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700">Our Bank Details</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="bank_details_type"
                        value="ecap"
                        checked={invoice.bank_details_type === 'ecap'}
                        onChange={(e) => setInvoice(prev => ({ ...prev, bank_details_type: e.target.value }))}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700">eCap Bank Details</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Remittance Email
                    <span className="ml-2 text-xs text-gray-500">(Where to send payment confirmations)</span>
                  </label>
                  <input
                    type="email"
                    value={invoice.remittance_email || ''}
                    onChange={(e) => setInvoice(prev => ({ ...prev, remittance_email: e.target.value }))}
                    placeholder="accounts@customer.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-indigo-500"
                  />
                </div>
              </div>
            </div>

            {/* Section 6: Terms & Notes */}
            <div className="bg-blue-50 rounded-lg shadow p-6 border-l-4 border-blue-400">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <span className="bg-blue-100 text-blue-700 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-3">6</span>
                Terms & Notes
              </h2>

              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700">Internal Notes</label>
                    <TextTemplatePicker
                      category="Invoices"
                      onInsert={(text) => {
                        const current = invoice.notes || '';
                        const newText = current ? current + '\n\n' + text : text;
                        setInvoice(prev => ({ ...prev, notes: newText }));
                      }}
                    />
                  </div>
                  <textarea
                    value={invoice.notes || ''}
                    onChange={(e) => setInvoice(prev => ({ ...prev, notes: e.target.value }))}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-indigo-500"
                    placeholder="Internal notes (not shown to customer)..."
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">Terms & Conditions</label>
                    <TextTemplatePicker
                      category="Invoices"
                      onInsert={(text) => {
                        const current = invoice.terms || '';
                        const newText = current ? current + '\n\n' + text : text;
                        setInvoice(prev => ({ ...prev, terms: newText }));
                      }}
                    />
                  </div>
                  <textarea
                    value={invoice.terms || ''}
                    onChange={(e) => setInvoice(prev => ({ ...prev, terms: e.target.value }))}
                    rows="5"
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-indigo-500"
                    placeholder="Terms and conditions shown on invoice..."
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar - 1/3 width - Sticky Summary */}
          <div className="lg:col-span-1">
            <div className="sticky top-4 space-y-4">

              {/* Warnings */}
              {isOverCreditLimit && (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
                  <div className="flex items-start">
                    <span className="text-red-600 text-xl mr-2">⚠️</span>
                    <div>
                      <h4 className="text-sm font-semibold text-red-800">Over Credit Limit</h4>
                      <p className="text-xs text-red-700 mt-1">
                        Invoice total (£{totals.total.toFixed(2)}) exceeds credit limit (£{parseFloat(selectedCustomer.credit_limit).toFixed(2)})
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Customer & Site Address */}
              {selectedCustomer && (
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="grid grid-cols-2 gap-4">
                    {/* Customer Column */}
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2 text-sm">Customer</h3>
                      <div className="text-xs space-y-1">
                        <div className="font-medium">{selectedCustomer.company_name}</div>
                        {selectedCustomer.account_reference && (
                          <div className="text-gray-500">Ref: {selectedCustomer.account_reference}</div>
                        )}
                        {selectedCustomer.contact_name && (
                          <div className="text-gray-600">{selectedCustomer.contact_name}</div>
                        )}
                        <div className="text-gray-600 mt-2">{selectedCustomer.address}</div>
                        <div className="text-gray-600">{selectedCustomer.city} {selectedCustomer.postcode}</div>
                      </div>
                    </div>

                    {/* Site Address Column */}
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2 text-sm">Site Address</h3>
                      <div className="text-xs space-y-1">
                        {selectedSite ? (
                          <>
                            <div className="font-medium">{selectedSite.site_name}</div>
                            <div className="text-gray-600">{selectedSite.address}</div>
                            <div className="text-gray-600">{selectedSite.city} {selectedSite.postcode}</div>
                          </>
                        ) : (
                          <div className="text-gray-500 italic">Same as customer address</div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Account Info */}
                  {(selectedCustomer.credit_limit || selectedCustomer.payment_terms) && (
                    <div className="mt-3 pt-3 border-t border-gray-200 grid grid-cols-2 gap-2">
                      {selectedCustomer.credit_limit && (
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-600">Credit Limit:</span>
                          <span className="font-medium">£{parseFloat(selectedCustomer.credit_limit).toFixed(2)}</span>
                        </div>
                      )}
                      {selectedCustomer.payment_terms && (
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-600">Payment Terms:</span>
                          <span className="font-medium">{selectedCustomer.payment_terms}</span>
                        </div>
                      )}
                    </div>
                  )}
                  {selectedCustomer.is_cis_contractor && (
                    <div className="mt-2">
                      <span className="inline-flex px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-xs font-medium">
                        CIS Contractor ({selectedCustomer.cis_rate || 20}%)
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* CIS Calculation (if applicable) */}
              {invoice.is_cis_invoice && invoice.cis_rate > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg shadow p-4">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <span className="text-orange-600 mr-2">🏗️</span>
                    CIS Deduction
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">CIS Rate</span>
                      <span className="font-medium">{invoice.cis_rate}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Materials (Subtotal)</span>
                      <span className="font-medium">£{totals.subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-orange-700">
                      <span className="font-medium">CIS Deduction</span>
                      <span className="font-bold">-£{invoice.cis_deduction?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-orange-300">
                      <span className="font-semibold text-gray-900">Net Payment (after CIS)</span>
                      <span className="font-bold text-lg text-orange-700">£{invoice.cis_net_payment?.toFixed(2) || '0.00'}</span>
                    </div>
                  </div>
                  <div className="mt-3 p-2 bg-orange-100 rounded text-xs text-orange-800">
                    <strong>Note:</strong> CIS deduction will be withheld and paid to HMRC on customer's behalf.
                  </div>
                </div>
              )}

              {/* Profit Breakdown */}
              <div className="bg-green-50 border border-green-200 rounded-lg shadow p-4">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <span className="text-green-600 mr-2">💰</span>
                  Profit
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Revenue (Subtotal)</span>
                    <span className="font-medium">£{totals.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Cost</span>
                    <span className="font-medium text-red-600">£0.00</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-green-300">
                    <span className="font-semibold text-gray-900">Profit</span>
                    <span className="font-bold text-lg text-green-700">£{totals.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-600">Margin</span>
                    <span className="font-medium text-green-600">100%</span>
                  </div>
                </div>
                <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-gray-600">
                  <strong>Note:</strong> Cost tracking coming soon - link line items to products with cost prices.
                </div>
              </div>

              {/* Financial Summary */}
              <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg shadow p-4 border border-indigo-200">
                <h3 className="font-semibold text-gray-900 mb-3">Summary</h3>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Parts</span>
                    <span className="font-medium">£{totals.partsTotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Labour</span>
                    <span className="font-medium">£{totals.labourTotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-indigo-300">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="font-medium">£{totals.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">VAT (20%)</span>
                    <span className="font-medium">£{totals.vatAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-indigo-300">
                    <span className="font-semibold text-gray-900">Total</span>
                    <span className="font-bold text-lg text-indigo-700">£{totals.total.toFixed(2)}</span>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-indigo-300 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Paid</span>
                    <span className="font-medium text-green-600">£{parseFloat(invoice.paid_amount || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Balance</span>
                    <span className={`font-bold ${(totals.total - parseFloat(invoice.paid_amount || 0)) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      £{(totals.total - parseFloat(invoice.paid_amount || 0)).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Product Selector Modal */}
      {showProductSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col relative">
            {/* Added notification */}
            {addedNotification && (
              <div className="absolute top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg z-10 animate-fade-in">
                ✓ {addedNotification}
              </div>
            )}

            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">Select Products</h3>
                <button
                  onClick={() => {
                    setShowProductSelector(false);
                    setProductSearch('');
                    setSelectedCategory('all');
                    setSelectedLetter('all');
                    setQuickQuantities({});
                  }}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="space-y-3">
                <input
                  type="text"
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  placeholder="Search by PART NUMBER, name, or description..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  autoFocus
                />

                {/* A-Z Letter Filter */}
                <div className="space-y-2">
                  <div className="flex gap-1 flex-wrap">
                    <button
                      onClick={() => { setSelectedLetter('all'); setSelectedCategory('all'); }}
                      className={`px-2 py-1 rounded text-xs font-medium transition ${
                        selectedLetter === 'all'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      All
                    </button>
                    {'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map(letter => (
                      <button
                        key={letter}
                        onClick={() => { setSelectedLetter(letter); setSelectedCategory('all'); }}
                        className={`px-2 py-1 rounded text-xs font-medium transition ${
                          selectedLetter === letter
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        {letter}
                      </button>
                    ))}
                  </div>

                  {/* Category Filter (filtered by letter) */}
                  {selectedLetter !== 'all' && (
                    <div className="flex gap-2 flex-wrap">
                      <span className="text-xs text-gray-600 py-1">Categories {selectedLetter}:</span>
                      {Array.from(new Set(
                        products
                          .filter(p => p.product_category)
                          .map(p => p.product_category)
                          .filter(cat => cat.toUpperCase().startsWith(selectedLetter))
                      )).sort().map(category => (
                        <button
                          key={category}
                          onClick={() => setSelectedCategory(category)}
                          className={`px-3 py-1 rounded text-xs font-medium transition ${
                            selectedCategory === category
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          {category}
                        </button>
                      ))}
                      {Array.from(new Set(
                        products
                          .filter(p => p.product_category)
                          .map(p => p.product_category)
                          .filter(cat => cat.toUpperCase().startsWith(selectedLetter))
                      )).length === 0 && (
                        <span className="text-xs text-gray-400 py-1">No categories starting with {selectedLetter}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-2">
                {products
                  .filter(p => p.is_active)
                  .filter(p => {
                    if (selectedCategory !== 'all') return p.product_category === selectedCategory;
                    if (selectedLetter === 'all') return true;
                    return p.product_category && p.product_category.toUpperCase().startsWith(selectedLetter);
                  })
                  .filter(p => {
                    if (!productSearch) return true;
                    const search = productSearch.toLowerCase();
                    return (
                      p.product_code?.toLowerCase().includes(search) ||
                      p.product_name?.toLowerCase().includes(search) ||
                      p.product_description?.toLowerCase().includes(search) ||
                      p.product_category?.toLowerCase().includes(search)
                    );
                  })
                  .sort((a, b) => {
                    // Prioritize product_code matches
                    if (!productSearch) return 0;
                    const search = productSearch.toLowerCase();
                    const aCodeMatch = a.product_code?.toLowerCase().includes(search);
                    const bCodeMatch = b.product_code?.toLowerCase().includes(search);
                    if (aCodeMatch && !bCodeMatch) return -1;
                    if (!aCodeMatch && bCodeMatch) return 1;
                    return 0;
                  })
                  .map(product => {
                    return (
                      <div
                        key={product.id}
                        className="p-4 border rounded border-gray-200 hover:bg-blue-50 hover:border-blue-300 transition"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-baseline gap-3 mb-1">
                              {product.product_code && (
                                <div className="text-base font-bold text-blue-600 font-mono bg-blue-50 px-2 py-0.5 rounded">
                                  {product.product_code}
                                </div>
                              )}
                              <div className="font-semibold text-gray-900">{product.product_name}</div>
                            </div>
                            {product.product_description && (
                              <div className="text-sm text-gray-600 mt-1">{product.product_description}</div>
                            )}
                            <div className="flex items-center gap-3 mt-2">
                              {product.product_category && (
                                <span className="text-xs text-gray-500">Category: {product.product_category}</span>
                              )}
                            </div>
                          </div>

                          <div className="text-right flex-shrink-0">
                            <div className="font-bold text-lg text-green-600 mb-2">
                              £{parseFloat(product.supply_price || product.price || 0).toFixed(2)}
                            </div>

                            <div className="flex items-center gap-2 mb-2">
                              <label className="text-xs text-gray-600">Qty:</label>
                              <input
                                type="number"
                                min="1"
                                value={quickQuantities[product.id] || 1}
                                onChange={(e) => setQuickQuantities({
                                  ...quickQuantities,
                                  [product.id]: parseInt(e.target.value) || 1
                                })}
                                onClick={(e) => e.stopPropagation()}
                                className="w-16 px-2 py-1 border border-gray-300 rounded text-sm text-center"
                              />
                            </div>

                            <button
                              onClick={() => addProductToInvoice(product)}
                              className="px-4 py-2 rounded font-medium text-sm transition bg-blue-600 hover:bg-blue-700 text-white"
                            >
                              Add to Invoice
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                {products.filter(p => p.is_active).filter(p => {
                  if (!productSearch) return true;
                  const search = productSearch.toLowerCase();
                  return (
                    p.product_code?.toLowerCase().includes(search) ||
                    p.product_name?.toLowerCase().includes(search) ||
                    p.product_description?.toLowerCase().includes(search) ||
                    p.product_category?.toLowerCase().includes(search)
                  );
                }).length === 0 && (
                  <div className="p-8 text-center text-gray-500">
                    {productSearch ? (
                      <>
                        <p className="text-lg">No products match "{productSearch}"</p>
                        <p className="text-sm mt-2">Try searching by part number or use "Add Manual Line" for custom items</p>
                      </>
                    ) : (
                      <>
                        <p>No products available</p>
                        <p className="text-sm mt-2">Add products in the Stock → Products section</p>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Labor Selector Modal */}
      {showLaborSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col relative">
            {addedNotification && (
              <div className="absolute top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg z-10 animate-fade-in">
                ✓ {addedNotification}
              </div>
            )}

            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">Select Labor</h3>
                <button
                  onClick={() => { setShowLaborSelector(false); setLaborSearch(''); setLaborQuantities({}); }}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>

              <input
                type="text"
                value={laborSearch}
                onChange={(e) => setLaborSearch(e.target.value)}
                placeholder="Search labor categories..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                autoFocus
              />
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-3">
                {laborCosts
                  .filter(labor =>
                    !laborSearch ||
                    labor.product_name?.toLowerCase().includes(laborSearch.toLowerCase()) ||
                    labor.product_code?.toLowerCase().includes(laborSearch.toLowerCase())
                  )
                  .map(labor => (
                    <div key={labor.id} className="bg-gray-50 rounded-lg p-4 hover:bg-blue-50 transition">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900">{labor.product_name}</div>
                          {labor.product_code && (
                            <div className="text-xs text-gray-500 mt-1">Code: {labor.product_code}</div>
                          )}
                        </div>

                        <div className="text-right flex-shrink-0">
                          <div className="font-bold text-lg text-green-600 mb-2">
                            £{parseFloat(labor.price || 0).toFixed(2)}/{labor.unit || 'hr'}
                          </div>

                          <div className="flex items-center gap-2 mb-2">
                            <label className="text-xs text-gray-600">Hours:</label>
                            <input
                              type="number"
                              min="0.5"
                              step="0.5"
                              value={laborQuantities[labor.id] || 1}
                              onChange={(e) => setLaborQuantities({
                                ...laborQuantities,
                                [labor.id]: parseFloat(e.target.value) || 1
                              })}
                              onClick={(e) => e.stopPropagation()}
                              className="w-16 px-2 py-1 border border-gray-300 rounded text-sm text-center"
                            />
                          </div>

                          <button
                            onClick={() => addLaborToInvoice(labor)}
                            className="px-4 py-2 rounded font-medium text-sm transition bg-blue-600 hover:bg-blue-700 text-white"
                          >
                            Add Labor
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}

                {laborCosts.filter(labor =>
                  !laborSearch ||
                  labor.product_name?.toLowerCase().includes(laborSearch.toLowerCase()) ||
                  labor.product_code?.toLowerCase().includes(laborSearch.toLowerCase())
                ).length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    {laborSearch ? 'No labor found matching your search.' : 'No labor services available.'}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Consumable Selector Modal */}
      {showConsumableSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col relative">
            {addedNotification && (
              <div className="absolute top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg z-10 animate-fade-in">
                ✓ {addedNotification}
              </div>
            )}

            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">Select Consumables</h3>
                <button
                  onClick={() => {
                    setShowConsumableSelector(false);
                    setConsumableSearch('');
                    setSelectedConsumableCategory('all');
                    setSelectedConsumableLetter('all');
                    setConsumableQuantities({});
                  }}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="space-y-3">
                <input
                  type="text"
                  value={consumableSearch}
                  onChange={(e) => setConsumableSearch(e.target.value)}
                  placeholder="Search by CODE, name, or category..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  autoFocus
                />

                {/* A-Z Letter Filter */}
                <div className="space-y-2">
                  <div className="flex gap-1 flex-wrap">
                    <button
                      onClick={() => { setSelectedConsumableLetter('all'); setSelectedConsumableCategory('all'); }}
                      className={`px-2 py-1 rounded text-xs font-medium transition ${
                        selectedConsumableLetter === 'all'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      All
                    </button>
                    {'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map(letter => (
                      <button
                        key={letter}
                        onClick={() => { setSelectedConsumableLetter(letter); setSelectedConsumableCategory('all'); }}
                        className={`px-2 py-1 rounded text-xs font-medium transition ${
                          selectedConsumableLetter === letter
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        {letter}
                      </button>
                    ))}
                  </div>

                  {/* Category Filter (filtered by letter) */}
                  {selectedConsumableLetter !== 'all' && (
                    <div className="flex gap-2 flex-wrap">
                      <span className="text-xs text-gray-600 py-1">Categories {selectedConsumableLetter}:</span>
                      {Array.from(new Set(
                        consumables
                          .filter(p => p.product_category)
                          .map(p => p.product_category)
                          .filter(cat => cat.toUpperCase().startsWith(selectedConsumableLetter))
                      )).sort().map(category => (
                        <button
                          key={category}
                          onClick={() => setSelectedConsumableCategory(category)}
                          className={`px-3 py-1 rounded text-xs font-medium transition ${
                            selectedConsumableCategory === category
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          {category}
                        </button>
                      ))}
                      {Array.from(new Set(
                        consumables
                          .filter(p => p.product_category)
                          .map(p => p.product_category)
                          .filter(cat => cat.toUpperCase().startsWith(selectedConsumableLetter))
                      )).length === 0 && (
                        <span className="text-xs text-gray-400 py-1">No categories starting with {selectedConsumableLetter}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-3">
                {consumables
                  .filter(item => {
                    // Search filter
                    if (consumableSearch && !(
                      item.product_name?.toLowerCase().includes(consumableSearch.toLowerCase()) ||
                      item.product_code?.toLowerCase().includes(consumableSearch.toLowerCase()) ||
                      item.product_category?.toLowerCase().includes(consumableSearch.toLowerCase())
                    )) return false;

                    // Letter filter
                    if (selectedConsumableLetter !== 'all') {
                      if (selectedConsumableCategory !== 'all') {
                        return item.product_category === selectedConsumableCategory;
                      }
                      return item.product_category?.toUpperCase().startsWith(selectedConsumableLetter);
                    }

                    return true;
                  })
                  .map(item => (
                    <div key={item.id} className="bg-gray-50 rounded-lg p-4 hover:bg-blue-50 transition">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-bold text-sm text-blue-600 mb-1">{item.product_code}</div>
                          <div className="font-semibold text-gray-900">{item.product_name}</div>
                          <div className="flex items-center gap-3 mt-2">
                            {item.product_category && (
                              <span className="text-xs text-gray-500">Category: {item.product_category}</span>
                            )}
                          </div>
                        </div>

                        <div className="text-right flex-shrink-0">
                          <div className="font-bold text-lg text-green-600 mb-2">
                            £{parseFloat(item.supply_price || item.price || 0).toFixed(2)}
                          </div>

                          <div className="flex items-center gap-2 mb-2">
                            <label className="text-xs text-gray-600">Qty:</label>
                            <input
                              type="number"
                              min="1"
                              value={consumableQuantities[item.id] || 1}
                              onChange={(e) => setConsumableQuantities({
                                ...consumableQuantities,
                                [item.id]: parseInt(e.target.value) || 1
                              })}
                              onClick={(e) => e.stopPropagation()}
                              className="w-16 px-2 py-1 border border-gray-300 rounded text-sm text-center"
                            />
                          </div>

                          <button
                            onClick={() => addConsumableToInvoice(item)}
                            className="px-4 py-2 rounded font-medium text-sm transition bg-blue-600 hover:bg-blue-700 text-white"
                          >
                            Add
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}

                {consumables.filter(item => {
                  if (consumableSearch && !(
                    item.product_name?.toLowerCase().includes(consumableSearch.toLowerCase()) ||
                    item.product_code?.toLowerCase().includes(consumableSearch.toLowerCase()) ||
                    item.product_category?.toLowerCase().includes(consumableSearch.toLowerCase())
                  )) return false;

                  if (selectedConsumableLetter !== 'all') {
                    if (selectedConsumableCategory !== 'all') {
                      return item.product_category === selectedConsumableCategory;
                    }
                    return item.product_category?.toUpperCase().startsWith(selectedConsumableLetter);
                  }

                  return true;
                }).length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    {consumableSearch || selectedConsumableLetter !== 'all'
                      ? 'No consumables found matching your filters.'
                      : 'No consumables available.'}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <DocumentTemplateSelector
        isOpen={showDocumentTemplateSelector}
        onClose={() => setShowDocumentTemplateSelector(false)}
        documentType="invoice"
        placementKey="invoice.print_pdf"
        data={invoice}
        onGenerate={handleDocumentTemplateGenerate}
        showEmailAction
      />

      <EmailModal
        show={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        onSend={handleSendEmail}
        documentType="Invoice"
        documentNumber={invoice.invoice_ref || invoice.id}
        customerEmail={customerEmail}
        templateData={emailTemplateData}
      />

    </div>
  );
}

export default InvoiceDetail;
